import { alpha, createTheme, ThemeOptions } from "@mui/material/styles";
import { grey, pink } from "@mui/material/colors";
import { greensparksMuiResponsiveFoundation } from "@greensparks/design-system/mui-theme";

export const appBreakpoints = greensparksMuiResponsiveFoundation.breakpoints;
export const appTouchTargetMin = greensparksMuiResponsiveFoundation.touchTargetMin;

export const getAppTheme = (mode: "light" | "dark") => {
  const isDark = mode === "dark";
  const primaryMain = isDark ? "#77dccb" : "#247f70";
  const primaryDark = isDark ? "#3aa894" : "#1b665a";
  const primaryLight = isDark ? "#a9eee3" : "#6ed7c7";
  const secondaryMain = isDark ? pink[200] : pink[600];
  const surfaceBorder = isDark ? "rgba(151, 178, 153, 0.14)" : alpha(grey[900], 0.08);
  const surfaceMuted = isDark ? "#161e1c" : "#fbfcfe";
  const paperBackground = isDark ? "#1a2320" : "#ffffff";
  const defaultBackground = isDark ? "#121817" : "#f4f7fb";
  const shellAppBarBackground = isDark ? "#0f1714" : "#244c35";
  const shellAppBarText = "#ffffff";
  const successTint = isDark ? alpha("#9bd86f", 0.2) : alpha("#36a743", 0.14);
  const dialogPaddingInline = 18;
  const dialogTitlePaddingTop = 14;
  const dialogTitlePaddingBottom = 8;
  const dialogContentPaddingTop = 12;
  const dialogContentPaddingBottom = 14;
  const dialogActionsPaddingBottom = 14;
  const dialogActionsGap = 8;

  const basePalette = {
    mode,
    primary: {
      main: primaryMain,
      dark: primaryDark,
      light: primaryLight,
    },
    secondary: {
      main: secondaryMain,
    },
    // Custom greensparks palette used for tree selection, etc.
    greensparks: {
      light: "#9bd86f",
      main: "#6cae50",
      dark: "#36a743",
    },
    background: {
      default: defaultBackground,
      paper: paperBackground,
    },
    text: {
      primary: isDark ? "#f4f7f1" : "#152033",
      secondary: isDark ? "rgba(232, 238, 229, 0.76)" : grey[700],
    },
    divider: surfaceBorder,
    action: {
      hover: isDark ? alpha("#9bd86f", 0.09) : alpha("#244c35", 0.08),
      selected: isDark ? alpha("#9bd86f", 0.16) : alpha("#244c35", 0.14),
      disabledBackground: isDark ? "rgba(244, 247, 241, 0.10)" : "rgba(0,0,0,0.08)",
      disabled: isDark ? "rgba(244, 247, 241, 0.34)" : "rgba(0,0,0,0.38)",
    },
  };

  const themeOptions: ThemeOptions = {
    breakpoints: appBreakpoints,
    palette: basePalette,
    shape: {
      borderRadius: 12,
    },
    typography: {
      fontFamily: '"Segoe UI", "Aptos", -apple-system, BlinkMacSystemFont, sans-serif',
      h6: {
        fontSize: "1rem",
        fontWeight: 600,
        letterSpacing: "0.01em",
      },
      h4: {
        fontSize: greensparksMuiResponsiveFoundation.typography.h4.fontSize,
        fontWeight: 600,
        [`@media (min-width:${appBreakpoints.values.md}px)`]: {
          fontSize: greensparksMuiResponsiveFoundation.typography.h4.md,
        },
        [`@media (min-width:${appBreakpoints.values.lg}px)`]: {
          fontSize: greensparksMuiResponsiveFoundation.typography.h4.lg,
        },
      },
      h5: {
        fontSize: greensparksMuiResponsiveFoundation.typography.h5.fontSize,
        fontWeight: 600,
        [`@media (min-width:${appBreakpoints.values.md}px)`]: {
          fontSize: greensparksMuiResponsiveFoundation.typography.h5.md,
        },
      },
      subtitle1: {
        fontWeight: 600,
      },
      button: {
        fontWeight: 600,
        letterSpacing: "0.01em",
        textTransform: "none",
      },
      body2: {
        lineHeight: 1.5,
      },
      caption: {
        lineHeight: 1.4,
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          ":root": {
            colorScheme: mode,
          },
          "html, body, #root": {
            height: "100%",
          },
          "*": {
            scrollbarWidth: "thin",
            scrollbarColor: `${alpha(primaryMain, 0.45)} transparent`,
          },
          "*::-webkit-scrollbar": {
            width: 10,
            height: 10,
          },
          "*::-webkit-scrollbar-thumb": {
            backgroundColor: alpha(primaryMain, isDark ? 0.4 : 0.28),
            borderRadius: 999,
            border: "2px solid transparent",
            backgroundClip: "padding-box",
          },
          "*::-webkit-scrollbar-track": {
            backgroundColor: "transparent",
          },
          body: {
            backgroundColor: defaultBackground,
            color: basePalette.text.primary,
            transition: "background-color 160ms ease, color 160ms ease",
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: shellAppBarBackground,
            color: shellAppBarText,
            borderBottom: `1px solid ${surfaceBorder}`,
            backgroundImage: "none",
          },
        },
      },
      MuiToolbar: {
        styleOverrides: {
          regular: {
            minHeight: 60,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            boxShadow: "none",
            minHeight: appTouchTargetMin,
            "&.Mui-disabled": {
              color: isDark ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.3)",
              backgroundColor: isDark
                ? "rgba(255,255,255,0.1)"
                : "rgba(0,0,0,0.1)",
            },
          },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: {
            color: isDark ? "#9bc4ff" : "#2367c8",
            "&:hover": {
              textDecoration: "underline",
            },
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            backgroundColor: isDark ? "#161e1c" : undefined,
            color: isDark ? "#fff" : undefined,
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          asterisk: {
            color: isDark ? "#ff8a80" : "#d32f2f",
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            backgroundColor: paperBackground,
            border: `1px solid ${surfaceBorder}`,
          },
          rounded: {
            borderRadius: 6,
          },
        },
      },
      MuiPopover: {
        defaultProps: {
          marginThreshold: 12,
        },
        styleOverrides: {
          paper: {
            backgroundImage: "none",
            backgroundColor: paperBackground,
            border: `1px solid ${surfaceBorder}`,
          },
        },
      },
      MuiMenu: {
        defaultProps: {
          slotProps: {
            paper: {
              elevation: 0,
            },
          },
        },
        styleOverrides: {
          paper: {
            marginTop: 6,
            minWidth: 180,
            backgroundImage: "none",
            backgroundColor: paperBackground,
          },
          list: {
            paddingTop: 6,
            paddingBottom: 6,
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            margin: "2px 6px",
            minHeight: appTouchTargetMin,
            paddingTop: 6,
            paddingBottom: 6,
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            minHeight: appTouchTargetMin,
            minWidth: appTouchTargetMin,
          },
        },
      },
      MuiDialog: {
        defaultProps: {
          PaperProps: {
            elevation: 0,
          },
        },
        styleOverrides: {
          paper: {
            backgroundImage: "none",
            backgroundColor: paperBackground,
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            padding: `${dialogTitlePaddingTop}px ${dialogPaddingInline}px ${dialogTitlePaddingBottom}px`,
            fontSize: "1rem",
            fontWeight: 600,
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            padding: `${dialogContentPaddingTop}px ${dialogPaddingInline}px ${dialogContentPaddingBottom}px`,
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: `0 ${dialogPaddingInline}px ${dialogActionsPaddingBottom}px`,
            gap: dialogActionsGap,
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: {
            minHeight: appTouchTargetMin,
          },
          indicator: {
            height: 3,
            borderRadius: 999,
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            minHeight: appTouchTargetMin,
            paddingTop: 8,
            paddingBottom: 8,
            textTransform: "none",
            fontWeight: 600,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 8,
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            backgroundColor: isDark ? alpha(surfaceMuted, 0.88) : alpha("#ffffff", 0.72),
            transition: "background-color 140ms ease, border-color 140ms ease",
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: alpha(primaryMain, 0.45),
            },
          },
          notchedOutline: {
            borderColor: surfaceBorder,
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            minHeight: appTouchTargetMin,
            paddingTop: 8,
            paddingBottom: 8,
            "&.Mui-selected": {
              backgroundColor: alpha(primaryMain, isDark ? 0.2 : 0.1),
              border: `1px solid ${alpha(primaryMain, isDark ? 0.3 : 0.18)}`,
            },
            "&.Mui-selected:hover": {
              backgroundColor: alpha(primaryMain, isDark ? 0.26 : 0.14),
            },
          },
        },
      },

      // 🔹 TABLE OVERRIDES
      MuiTableCell: {
        styleOverrides: {
          head: {
            backgroundColor: isDark ? "#0f1714" : grey[100],
            color: isDark ? "#f4f7f1" : grey[900],
            fontWeight: 600,
            fontSize: "12px",
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            borderBottom: `1px solid ${isDark ? "rgba(151, 178, 153, 0.18)" : grey[300]}`,
          },
          body: {
            color: isDark ? "#f4f7f1" : grey[900],
            borderBottom: `1px solid ${isDark ? "rgba(151, 178, 153, 0.12)" : grey[300]}`,
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            // Alternate row striping
            "&:nth-of-type(odd)": {
              backgroundColor: isDark ? "#151d1a" : "#fcfdff",
            },
            "&:nth-of-type(even)": {
              backgroundColor: isDark ? "#1a2320" : "#f6f8fc",
            },

            // Hover override (always takes precedence)
            "&:hover": {
              backgroundColor: isDark
                ? alpha(primaryMain, 0.12)
                : "#eefbea",
            },
          },
        },
      },
      MuiTablePagination: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? "#0f1714" : grey[50],
            color: isDark ? "#f4f7f1" : grey[900],
            borderTop: `1px solid ${isDark ? "rgba(151, 178, 153, 0.18)" : grey[300]}`,
          },
          selectIcon: {
            color: isDark ? "#f4f7f1" : grey[900],
          },
        },
      },
      MuiSlider: {
        styleOverrides: {
          rail: {
            opacity: 1,
            backgroundColor: alpha(basePalette.text.primary, isDark ? 0.18 : 0.12),
          },
          track: {
            border: "none",
          },
          thumb: {
            boxShadow: `0 0 0 6px ${successTint}`,
          },
        },
      },
    },
  };

  return createTheme(themeOptions);
};
